/*customization start*/
/* Author:        Jim MacLain (MacLaiJ@tompkinscortland.edu)/Jorge Reategui (JR150@tompkinscortland.edu)
 * Client:        Tompkins Cortland Community College
 * Customization: Teaching Mode Modal
 * Date: 		  04-26-2022
 * Description:   Modal content for definitions and explanations of Teaching Modalities
 */

// #region Imports
import React from 'react';

// Core components
import Button from '@hedtech/powercampus-design-system/react/core/Button';
import Grid from '@hedtech/powercampus-design-system/react/core/Grid';
import Modal from '@hedtech/powercampus-design-system/react/core/Modal';

// Helpers
import { createStyles, Theme, WithStyles, withStyles } from '@hedtech/powercampus-design-system/react/core/styles/withStyles';

// #region Types
export interface ITeachingModeDescModalProps {
    onCloseModal: () => void;
    open: boolean;
}
// #endregion Types

const styles = ((theme: Theme) => createStyles({
    table: {
        [theme.breakpoints.up('md')]: {
            // Width
            '& > thead > tr > th:nth-child(1)': {
                width: '25%'
            },
            '& > thead > tr > th:nth-child(4)': {
                width: '20%'
            }
        }
    },
    tableCell: {
        textAlign: 'center'
    },

    uList: {
        fontFamily: 'Noto Sans, Helvetica, Arial, sans-serif'
    },
    h4: {
        fontFamily: 'Noto Sans, Helvetica, Arial, sans-serif'
    }
}));

type PropsWithStyles = ITeachingModeDescModalProps & WithStyles<typeof styles>;


// #region Component
const TeachingModeDescModal: React.FC<PropsWithStyles> = (props: PropsWithStyles): JSX.Element => {
    const {
        open,
        classes,
        onCloseModal
    } = props;

    return (
        <Modal
            disableBackdropClick
            footer={(
                <Button
                    id={'btnClose'}
                    onClick={onCloseModal}
                >
                    Close
                </Button>
            )}
            id="TeachingModeDescModalLink"
            header={"Course Teaching Modes Explained"}
            maxWidth="lg"
            open={open}
            onClose={onCloseModal}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} md={12} >

                    <h4 className={classes.h4}>Course Teaching Modes</h4>
                    <ul className={classes.uList}>
                        <li><strong>On-Campus Meetings:</strong> Class instruction is 100% on-campus synchronous.</li>
                        <li><strong>On-Campus and Live Online Meetings Blend:</strong> Class instruction is on-campus synchronous meetings and online synchronous. In person may be optional for some courses.
                        </li>
                        <li><strong>On-Campus and Online Asynchronous Blend:</strong> Class instruction is a blend of on-campus synchronous meetings and online asynchronous (instruction not occurring at a specified meeting time). In person may be optional for some courses.</li>
                        <li><strong>Live Online Meetings:</strong> Class instruction is 100% online synchronous.</li>
                        <li><strong>Live Online and Online Asynchronous Blend:</strong> Class instruction is a blend of online synchronous meetings and online asynchronous (instruction not occurring at a specified meeting time).</li>
                        <li><strong>Online Asynchronous:</strong> Class instruction is 100% online asynchronous (instruction not occurring at a specified meeting time).   </li>
                        <li><strong>Online Flex:</strong> Students have the option of attending class sessions live online or watching recordings of the live online sessions later</li>
                        <li><strong>HyFlex:</strong>Combines online and face-to-face instruction simultaneously into one single course section. Students are able to participate in class in different ways: as a synchronous distance learner (via real-time, video-streaming); as an asynchronous distance learner (accessing materials, recorded lectures, and responding at a later time); as a face-to-face learner (physically present in the classroom); or as a flexible learner (with a degree of choice as to how they participate each week; sometimes face-to-face, sometimes by streaming class sessions, etc.).</li>
                    </ul>
                </Grid>
                <Grid item xs={12} md={12}>
                    <h4 className={classes.h4}>Definitions</h4>
                    <ul className={classes.uList}>
                        <li><strong>Asynchronous Instruction:</strong>  Direct Instruction Occurs online and not in a specified meeting time.</li>
                        <li><strong>Synchronous Instruction:</strong>  Direct Instruction takes place at a specified meeting time. </li>
                    </ul>
                </Grid>
            </Grid>
        </Modal>
    );
};

// Export: Component
export default withStyles(styles)(TeachingModeDescModal);
/*customization end*/